import { designVariables } from "@studio3t/design-variables";
import {
  ButtonMediumError,
  ButtonMediumPrimary,
  ButtonMediumSecondary,
} from "@studio3t/generic-components";
import { type ReactNode, useId } from "react";
import styled from "styled-components";
import {
  ModalBackdrop,
  ModalBody,
  ModalCard,
  ModalFooter,
  ModalFooterActions,
  ModalFooterGroup,
  ModalHeader,
  ModalTitle,
} from "@/components/ModalChrome";
import { ModalCloseButton } from "@/components/ModalCloseButton";
import { useModalFocus } from "@/hooks/useModalFocus";
import { useModalKeys } from "@/hooks/useModalKeys";

const MODAL = designVariables.ui.modal;

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  /** Confirm button palette — non-destructive confirms (e.g. the schema
   *  save warning) use "primary" instead of the default red. */
  confirmType?: "error" | "primary";
  loading?: boolean;
  loadingLabel?: string;
  error?: string | null;
  /** Optional middle action between Cancel and the confirm (e.g. the
   *  schema save warning's "Save as"). */
  extraAction?: { label: string; onClick: () => void };
  /** Where the extra action sits. "right" (default) is the app's macOS
   *  split — Cancel alone left, the action pair right; "left" mirrors it
   *  for modals whose Figma keeps Cancel beside the confirm (the
   *  switch-pipeline modal, 14896-126480). */
  extraActionSide?: "left" | "right";
  /** Acknowledgement-only modals (nothing to confirm or back out of) drop
   *  the Cancel button; Escape, × and the backdrop still dismiss. */
  hideCancel?: boolean;
  /** Overrides the dismissing button's "Cancel" (e.g. the unsaved-work
   *  warning's "Save your work" — dismissing means going back to save). */
  cancelLabel?: string;
  /** The card's width cap, px (default 440); e.g. the switch-pipeline
   *  modal's 600 (Figma Modal demo 14896-126480). */
  maxWidth?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDeleteModal({
  isOpen,
  title,
  message,
  confirmLabel = "Delete",
  confirmType = "error",
  loading = false,
  loadingLabel = "Deleting…",
  error = null,
  extraAction,
  extraActionSide = "right",
  hideCancel = false,
  cancelLabel = "Cancel",
  maxWidth = 440,
  onConfirm,
  onCancel,
}: ConfirmDeleteModalProps) {
  const modalRef = useModalFocus(isOpen);
  const modalTitleId = useId();

  // Escape cancels, Enter confirms (useModalKeys). Both stay shut while
  // the action is in flight, matching the disabled buttons — a second
  // Enter cannot fire a second delete.
  useModalKeys({ active: isOpen && !loading, onCancel, onConfirm });

  if (!isOpen) return null;

  const ConfirmButton =
    confirmType === "error" ? ButtonMediumError : ButtonMediumPrimary;
  const confirm = (
    <ConfirmButton
      text={loading ? loadingLabel : confirmLabel}
      onClick={onConfirm}
      disabled={loading}
    />
  );
  const cancel = hideCancel ? null : (
    <ButtonMediumSecondary
      text={cancelLabel}
      onClick={onCancel}
      disabled={loading}
    />
  );

  return (
    <ModalBackdrop onClick={loading ? undefined : onCancel} role="presentation">
      <Modal
        ref={modalRef}
        $maxWidth={maxWidth}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        tabIndex={-1}
        data-testid="confirm-delete-modal"
      >
        <ModalHeader>
          <ModalTitle id={modalTitleId}>{title}</ModalTitle>
          <ModalCloseButton
            $top={MODAL.header.paddingh}
            $right={MODAL.paddingw}
            onClick={onCancel}
            disabled={loading}
            aria-label="Close modal"
          />
        </ModalHeader>

        <Body>
          <Message>{message}</Message>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Body>

        <ModalFooter>
          {/* macOS button layout: with a middle action, the dismissing
              Cancel sits alone on the left and the action pair on the
              right; plain two-button modals keep both on the right,
              cancel left of accept. No Cancel, no split: the pair alone
              would otherwise sit on the left. */}
          <ModalFooterActions $split={extraAction !== undefined && !hideCancel}>
            <FooterButtons
              extraAction={extraAction}
              extraActionSide={extraActionSide}
              loading={loading}
              cancel={cancel}
              confirm={confirm}
            />
          </ModalFooterActions>
        </ModalFooter>
      </Modal>
    </ModalBackdrop>
  );
}

// The footer's button order. Default: the app's macOS split — the
// dismissing cancel left of the action pair. "left" mirrors it for the
// modals whose Figma keeps Cancel beside the confirm.
function FooterButtons({
  extraAction,
  extraActionSide,
  loading,
  cancel,
  confirm,
}: {
  extraAction: ConfirmDeleteModalProps["extraAction"];
  extraActionSide: "left" | "right";
  loading: boolean;
  cancel: ReactNode;
  confirm: ReactNode;
}) {
  if (!extraAction) {
    return (
      <>
        {cancel}
        {confirm}
      </>
    );
  }
  const extra = (
    <ButtonMediumSecondary
      text={extraAction.label}
      onClick={extraAction.onClick}
      disabled={loading}
    />
  );
  if (extraActionSide === "left") {
    return (
      <>
        {extra}
        <ModalFooterGroup>
          {cancel}
          {confirm}
        </ModalFooterGroup>
      </>
    );
  }
  return (
    <>
      {cancel}
      <ModalFooterGroup>
        {extra}
        {confirm}
      </ModalFooterGroup>
    </>
  );
}

// min() keeps ModalCard's own responsive cap (100vw - 40px) in force
// under the configurable width.
const Modal = styled(ModalCard)<{ $maxWidth: number }>`
  width: 100%;
  max-width: min(${(p) => p.$maxWidth}px, calc(100vw - 40px));
`;

const Body = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// A div, not a p: messages may hold their own paragraphs and inputs.
const Message = styled.div`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${(p) => p.theme.semanticTextBody};
  overflow-wrap: anywhere;

  & > p {
    margin: 0;
  }

  & > p + p {
    margin-top: 8px;
  }
`;

const ErrorMessage = styled.div`
  padding: 10px 12px;
  background: ${(p) => p.theme.badgeErrorSubtleBackground};
  border-radius: 4px;
  color: ${(p) => p.theme.badgeErrorSubtleColor};
  font-size: 13px;
`;

export default ConfirmDeleteModal;
